import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import InfoPage from "../InfoPage";
import { getAuthToken, decodeToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import { uploadFile, validateFile } from '../../utils/fileUpload';
import { displayHandle as fmtHandle } from '../../utils/streamHelpers';
import ModalPortal from '../../components/Modal/ModalPortal';
import './Storefronts.sass';

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const PLATFORMS = [
  { key: 'ltk', label: 'LTK' },
  { key: 'amazon', label: 'Amazon' },
  { key: 'rockporch', label: 'Rockporch' },
  { key: 'linktree', label: 'Linktree' },
  { key: 'stan_store', label: 'Stan Store' },
  { key: 'shopmy', label: 'ShopMy' },
  { key: 'other', label: 'Other' },
];

const platformLabel = (key) => {
  const found = PLATFORMS.find(p => p.key === key);
  return found ? found.label : key;
};

const Card = ({ img, handle, onClick }) => {
  const [imageFailed, setImageFailed] = useState(false);

  if (!img || imageFailed) return null;

  return (
    <div className="sf-card" style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div className="sf-card-image-wrapper">
        <img
          src={img}
          alt={handle}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={() => setImageFailed(true)}
        />
      </div>
      <div className="sf-card-name">
        <span>{handle}</span>
      </div>
    </div>
  );
};

const ViewModal = ({ isOpen, onClose, storefront, isOwner, onEdit, onDelete }) => {
  if (!storefront) return null;

  const links = Array.isArray(storefront.links) ? storefront.links : [];
  const displayName = storefront.property_name
    ? storefront.property_name
    : storefront.handle
      ? fmtHandle(storefront.handle)
      : storefront.brand_name || storefront.first_name || 'User';

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="480px" maxHeight="80vh">
      {storefront.avatar_image_url && (
        <div className="sf-modal-header">
          <img src={storefront.avatar_image_url} alt={displayName} />
        </div>
      )}
      <div className="sf-modal-content">
        <h2 className="sf-modal-title">{displayName}</h2>

        {links.length > 0 ? (
          <div className="sf-modal-links">
            {links.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-modal-link-btn"
              >
                {links.length === 1 ? 'View my Storefront' : platformLabel(link.platform)}
              </a>
            ))}
          </div>
        ) : (
          <p className="sf-modal-empty">No storefront links yet.</p>
        )}

        {isOwner && (
          <div className="sf-modal-owner-actions">
            <button className="sf-btn-edit" onClick={onEdit}>Edit</button>
            <button
              className="sf-btn-delete"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete your storefront?')) {
                  onDelete();
                }
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </ModalPortal>
  );
};

const Storefronts = ({ type = 'creator' }) => {
  const navigate = useNavigate();
  const [storefronts, setStorefronts] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [authorized, setAuthorized] = useState(null);

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedStorefront, setSelectedStorefront] = useState(null);

  // Post/Edit modal state
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [linkFields, setLinkFields] = useState(
    PLATFORMS.map(p => ({ platform: p.key, url: '', enabled: false }))
  );

  // Host property selection
  const [userProperties, setUserProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  // Custom image upload
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  const pageTitle = type === 'creator' ? 'Storefronts' : 'Shop Our Properties';
  const pageDescription = type === 'creator'
    ? 'Browse creator storefronts — shop their favorite products, tools, and recommendations.'
    : 'Browse host storefronts — shop property essentials, decor, and curated collections.';
  const ctaLabel = type === 'creator' ? 'Post Your Storefront' : 'Add a Property Storefront';
  const requiredRole = type === 'creator' ? 'creator' : 'host';

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded?.userId) setCurrentUserId(decoded.userId);
    }

    let e = '';
    try { e = localStorage.getItem('postaway_email') || ''; } catch (_) {}

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: e })
        });
        const data = await res.json();
        if (res.ok && data.ok && data.active) {
          setAuthorized(true);
          setUserRoles(data.roles || []);
        } else {
          setAuthorized(true);
          setUserRoles(data.roles || []);
        }
      } catch (err) {
        setAuthorized(true);
        setUserRoles([]);
      }
    })();
  }, [navigate]);

  // Load host properties when type is host
  useEffect(() => {
    if (type !== 'host' || !userRoles.includes('host')) return;
    let e = '';
    try { e = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    if (!e) return;

    (async () => {
      try {
        const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const res = await fetch(`${base}/api/properties/list?email=${encodeURIComponent(e)}`);
        const j = await res.json();
        if (res.ok && j.ok) {
          setUserProperties(j.properties || []);
        }
      } catch (_) {}
    })();
  }, [type, userRoles]);

  const fetchStorefronts = async () => {
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await fetch(`${base}/api/storefronts/list?type=${type}`);
      const json = await res.json();
      if (res.ok && json.ok) {
        setStorefronts(shuffleArray(json.storefronts || []));
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchStorefronts();
  }, [type]);

  const resetLinkFields = () => {
    setLinkFields(PLATFORMS.map(p => ({ platform: p.key, url: '', enabled: false })));
  };

  const openPostModal = () => {
    resetLinkFields();
    setSelectedPropertyId('');
    setCustomImageUrl('');
    setIsEditMode(false);
    setPostModalOpen(true);
  };

  const openEditModal = (storefront) => {
    const links = Array.isArray(storefront.links) ? storefront.links : [];
    setLinkFields(PLATFORMS.map(p => {
      const existing = links.find(l => l.platform === p.key);
      return { platform: p.key, url: existing?.url || '', enabled: !!existing };
    }));
    setSelectedPropertyId(storefront.property_id || '');
    setCustomImageUrl(storefront.custom_image_url || '');
    setIsEditMode(true);
    setViewModalOpen(false);
    setPostModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateFile(file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif']
    });
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    setImageUploading(true);
    try {
      const url = await uploadFile(file);
      setCustomImageUrl(url);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    const links = linkFields
      .filter(f => f.enabled && f.url.trim())
      .map(f => ({ platform: f.platform, url: f.url.trim() }));

    if (links.length === 0) {
      alert('Please add at least one storefront link.');
      return;
    }

    if (type === 'host' && !selectedPropertyId) {
      alert('Please select a property for this storefront.');
      return;
    }

    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/storefronts/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, links, property_id: selectedPropertyId || null, custom_image_url: customImageUrl || null })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setPostModalOpen(false);
        setIsEditMode(false);
        resetLinkFields();
        await fetchStorefronts();
      } else {
        alert('Failed to save storefront. Please try again.');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        alert('Failed to save storefront. Please try again.');
      }
    }
  };

  const handleDelete = async (storefront) => {
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/storefronts/delete?id=${storefront.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setViewModalOpen(false);
        setSelectedStorefront(null);
        await fetchStorefronts();
      } else {
        alert('Failed to delete storefront.');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        alert('Failed to delete storefront.');
      }
    }
  };

  if (authorized === null) {
    return (
      <InfoPage
        belowHeader={
          <div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>
            Loading…
          </div>
        }
        disableStamps
        tallOverride={false}
        customMinHeight={600}
        showInlineLivePulse
      />
    );
  }

  const myStorefronts = storefronts.filter(sf => sf.user_id && currentUserId && String(sf.user_id) === String(currentUserId));
  const myPropertyIds = new Set(myStorefronts.map(sf => sf.property_id).filter(Boolean));
  const availableProperties = userProperties.filter(p => !myPropertyIds.has(p.id));
  const canPost = type === 'creator'
    ? (userRoles.includes('creator') && myStorefronts.length === 0)
    : (userRoles.includes('host') && availableProperties.length > 0);

  const below = (
    <div className="sf-page">
      {canPost && (
        <div className="sf-add-button">
          <button className="button" type="button" onClick={openPostModal} style={{ cursor: 'pointer' }}>
            {ctaLabel}
          </button>
        </div>
      )}

      <div className="sf-description">
        <p>{pageDescription}</p>
      </div>

      <div className="sf-grid">
        {storefronts.map((sf, i) => {
          const rawUrl = sf.avatar_image_url;
          if (!rawUrl) return null;

          const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
          let resolved = rawUrl;
          if (resolved.startsWith('/') && isLocal) {
            resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
          }

          const optimizedImage = getCloudinaryUrl(resolved, {
            width: 450,
            height: 600,
            crop: 'fill',
            quality: 'auto',
            format: 'auto'
          });

          const displayName = type === 'host' && sf.property_name
            ? sf.property_name
            : sf.handle
              ? fmtHandle(sf.handle)
              : sf.brand_name || sf.property_name || sf.first_name || 'User';

          return (
            <Card
              key={sf.id || i}
              img={optimizedImage}
              handle={displayName}
              onClick={() => {
                setSelectedStorefront({ ...sf, avatar_image_url: resolved });
                setViewModalOpen(true);
              }}
            />
          );
        })}
      </div>

      {storefronts.length === 0 && (
        <div className="sf-empty">
          <p>No storefronts posted yet. Be the first!</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <InfoPage below={below} disableStamps tallOverride={false} customMinHeight={720} showInlineLivePulse />

      <ViewModal
        isOpen={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setSelectedStorefront(null); }}
        storefront={selectedStorefront}
        isOwner={selectedStorefront?.user_id && currentUserId && String(selectedStorefront.user_id) === String(currentUserId)}
        onEdit={() => openEditModal(selectedStorefront)}
        onDelete={() => handleDelete(selectedStorefront)}
      />

      {postModalOpen && (
        <div className="sf-post-overlay" onClick={() => { setPostModalOpen(false); setIsEditMode(false); }}>
          <div className="sf-post-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isEditMode ? 'Edit Your Storefront' : 'Post Your Storefront'}</h3>
            <p className="sf-post-hint">Toggle each platform and paste your link.</p>

            {type === 'host' && (isEditMode ? userProperties : availableProperties).length > 0 && (
              <div className="sf-post-property-select">
                <label>Select a property <span style={{ color: '#dc1d08' }}>*</span></label>
                <select value={selectedPropertyId} onChange={(e) => setSelectedPropertyId(e.target.value)} required>
                  <option value="">Choose a property…</option>
                  {(isEditMode ? userProperties : availableProperties).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || 'Property'}{(() => {
                        const parts = [];
                        if (p.location_city) parts.push(p.location_city);
                        if (p.location_state) parts.push(p.location_state);
                        if (p.location_country && p.location_country !== 'United States') parts.push(p.location_country);
                        return parts.length ? ` — ${parts.join(', ')}` : '';
                      })()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="sf-post-image-upload">
              <label>Custom Image (optional)</label>
              <p className="sf-post-image-hint">Upload a custom image for your storefront card. If not provided, your profile picture will be used.</p>
              {customImageUrl ? (
                <div className="sf-post-image-preview">
                  <img src={getCloudinaryUrl(customImageUrl, { width: 200, height: 267, crop: 'fill', quality: 'auto', format: 'auto' })} alt="Preview" />
                  <button type="button" className="sf-post-image-remove" onClick={() => setCustomImageUrl('')}>Remove</button>
                </div>
              ) : (
                <button
                  type="button"
                  className="sf-post-image-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'Uploading...' : 'Choose Image'}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </div>

            <div className="sf-post-fields">
              {linkFields.map((field, i) => (
                <div key={field.platform} className="sf-post-row">
                  <label className="sf-post-toggle">
                    <input
                      type="checkbox"
                      checked={field.enabled}
                      onChange={(e) => {
                        const updated = [...linkFields];
                        updated[i] = { ...updated[i], enabled: e.target.checked };
                        setLinkFields(updated);
                      }}
                    />
                    <span>{platformLabel(field.platform)}</span>
                  </label>
                  {field.enabled && (
                    <input
                      type="url"
                      value={field.url}
                      onChange={(e) => {
                        const updated = [...linkFields];
                        updated[i] = { ...updated[i], url: e.target.value };
                        setLinkFields(updated);
                      }}
                      placeholder={`https://...`}
                      className="sf-post-url"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="sf-post-actions">
              <button
                className="button-stroke"
                type="button"
                onClick={() => { setPostModalOpen(false); setIsEditMode(false); }}
                style={{ cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="button"
                type="button"
                onClick={handleSubmit}
                style={{ cursor: 'pointer' }}
              >
                {isEditMode ? 'Save Changes' : 'Post Storefront'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Storefronts;
